import React, { useCallback, useRef, useEffect } from 'react';
import VideoPlaceholder from './VideoPlaceholder';
import SidePanel from './SidePanel';
import StartVideoButton from './StartVideoButton';
import {
  PipecatClientAudio,
  PipecatClientVideo,
  usePipecatClient,
  usePipecatClientTransportState,
  useRTVIClientEvent,
} from "@pipecat-ai/client-react";
import { RTVIEvent } from "@pipecat-ai/client-js";
import { PipecatProvider } from "../providers/PipecatProvider";
import { DebugDisplay } from './DebugDisplay';
import './VideoPopup.css';

// Inner component that has access to Pipecat hooks
const VideoPopupContent = ({ onClose, onStartVideoChat, translations, botConfig, botInfo }) => {
  const [isInitiatingCall, setIsInitiatingCall] = React.useState(false);
  const [connectionError, setConnectionError] = React.useState(null);
  const [retryHandler, setRetryHandler] = React.useState(null);
  const [showcaseImages, setShowcaseImages] = React.useState([]);
  const [showcaseCategory, setShowcaseCategory] = React.useState('');
  const [directConnectHandler, setDirectConnectHandler] = React.useState(null);
  const [availableMicrophones, setAvailableMicrophones] = React.useState([]);
  const [selectedMicrophone, setSelectedMicrophone] = React.useState(null);
  const [showMicrophoneDropdown, setShowMicrophoneDropdown] = React.useState(false);
  const startButtonRef = useRef(null);
  const client = usePipecatClient();
  const transportState = usePipecatClientTransportState();
  const isConnected = ["connected", "ready"].includes(transportState);

  // Listen for showcase images messages from bot
  useRTVIClientEvent(
    RTVIEvent.ServerMessage,
    useCallback((message) => {
      console.log('VideoPopup: Server message received:', message);

      if (message?.type === 'showcase_images') {
        if (message.action === 'clear') {
          console.log('VideoPopup: Clearing showcase images');
          setShowcaseImages([]);
          setShowcaseCategory('');
        } else if (message.action === 'display') {
          console.log('VideoPopup: Displaying showcase images:', message.images);
          setShowcaseImages(message.images || []);
          setShowcaseCategory(message.category || '');
        }
      }
    }, [])
  );

  const handleStartCall = () => {
    console.log('Button clicked - initiating call');

    // Show calling screen (CallingScreen handles its own audio)
    setIsInitiatingCall(true);
    setConnectionError(null);

    if (onStartVideoChat) {
      onStartVideoChat();
    }
  };

  const handleConnectionError = (error) => {
    console.log('Connection error received:', error);
    setConnectionError(error);
    setIsInitiatingCall(false);
  };

  const handleRetry = () => {
    console.log('Retry clicked - attempting new connection');

    // Call the actual StartVideoButton handler if available
    if (retryHandler) {
      console.log('Calling StartVideoButton handler directly');
      retryHandler();
    } else {
      console.log('No retry handler available, falling back to basic flow');
      setConnectionError(null);
      setIsInitiatingCall(true);
      if (onStartVideoChat) {
        onStartVideoChat();
      }
    }
  };

  const handleRetryConnectionSetup = (handler) => {
    setRetryHandler(() => handler);
  };

  const handleClose = async () => {
    // Clear showcase images
    setShowcaseImages([]);
    setShowcaseCategory('');

    // Disconnect from Pipecat if connected
    if (client && isConnected) {
      try {
        console.log('Disconnecting Pipecat client before closing popup');
        await client.disconnect();
      } catch (error) {
        console.error("Error disconnecting client:", error);
      }
    }

    // Call the original onClose function
    onClose();
  };

  // Enumerate microphones on mount
  useEffect(() => {
    const getMicrophones = async () => {
      try {
        // Request microphone permission first
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // Get all audio input devices
        const devices = await navigator.mediaDevices.enumerateDevices();
        const microphones = devices.filter(device => device.kind === 'audioinput');

        setAvailableMicrophones(microphones);

        // Set default microphone (usually the first one or default)
        const defaultMic = microphones.find(mic => mic.deviceId === 'default') || microphones[0];
        if (defaultMic) {
          setSelectedMicrophone(defaultMic);
        }

      } catch (error) {
        console.error('Error getting microphones:', error);
        // Set a fallback option if permission denied
        setAvailableMicrophones([{ deviceId: 'default', label: 'Default Microphone' }]);
        setSelectedMicrophone({ deviceId: 'default', label: 'Default Microphone' });
      }
    };

    getMicrophones();
  }, []);

  // Handle microphone selection
  const handleMicrophoneSelect = async (microphone) => {
    try {
      // Request permission for the specific microphone device
      const constraints = {
        audio: {
          deviceId: microphone.deviceId ? { exact: microphone.deviceId } : undefined,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      };

      await navigator.mediaDevices.getUserMedia(constraints);
      setSelectedMicrophone(microphone);
      setShowMicrophoneDropdown(false);

      console.log('Microphone selected:', microphone.label);
    } catch (error) {
      console.error('Error selecting microphone:', error);
      alert('Unable to access the selected microphone. Please check your permissions.');
    }
  };

  // Check if side panel should be shown (defaults to true for backwards compatibility)
  const showSidePanel = botInfo?.showSidePanel !== false;

  return (
    <div className="video-popup-overlay">
      <div className={`video-popup-modal ${!showSidePanel ? 'no-side-panel' : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="video-popup-header">
          <div className="video-popup-title">{botInfo?.name || translations?.olivioAIAssistant || 'Olivio AI Assistant'}</div>
          <button className="video-popup-close" onClick={handleClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>
        <div className={`video-popup-content ${!showSidePanel ? 'full-width' : ''}`}>
          {/* Video Section (Left/Top) */}
          <VideoPlaceholder
            isInitiatingCall={isInitiatingCall}
            setIsInitiatingCall={setIsInitiatingCall}
            connectionError={connectionError}
            onRetry={handleRetry}
          />
          {/*<DebugDisplay />*/}
          <PipecatClientAudio />

          {/* Side Panel (Right/Bottom) - conditionally rendered */}
          {showSidePanel ? (
            <SidePanel
              onStartVideoChat={handleStartCall}
              onConnectionError={handleConnectionError}
              onRetryConnection={handleRetryConnectionSetup}
              translations={translations}
              botConfig={botConfig}
              botInfo={botInfo}
            />
          ) : (
            /* Floating start button and microphone selector when no side panel */
            <>
              {/* Hidden StartVideoButton to handle connection logic */}
              <div style={{ display: 'none' }}>
                <StartVideoButton
                  onClick={handleStartCall}
                  onConnectionError={handleConnectionError}
                  onRetryConnection={(handler) => {
                    setDirectConnectHandler(() => handler);
                    handleRetryConnectionSetup(handler);
                  }}
                  botConfig={botConfig}
                />
              </div>

              {/* Microphone Selector - Top Right */}
              <div className="floating-mic-selector">
                <button
                  className="mic-selector-button"
                  onClick={() => setShowMicrophoneDropdown(!showMicrophoneDropdown)}
                  title="Select Microphone"
                >
                  <i className="fas fa-microphone"></i>
                </button>
                {showMicrophoneDropdown && (
                  <div className="mic-dropdown-floating">
                    {availableMicrophones.map((microphone, index) => (
                      <button
                        key={microphone.deviceId || index}
                        className={`mic-dropdown-item ${selectedMicrophone?.deviceId === microphone.deviceId ? 'selected' : ''}`}
                        onClick={() => handleMicrophoneSelect(microphone)}
                      >
                        <i className="fas fa-microphone"></i>
                        <span>{microphone.label || `Microphone ${index + 1}`}</span>
                        {selectedMicrophone?.deviceId === microphone.deviceId && (
                          <i className="fas fa-check"></i>
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {!isInitiatingCall && !connectionError && !isConnected && (
                <div className="floating-start-button">
                  <button
                    className="start-call-floating"
                    onClick={() => {
                      if (directConnectHandler) {
                        directConnectHandler();
                      }
                    }}
                  >
                    <i className="fas fa-video"></i>
                    <span>Start Conversation</span>
                  </button>
                </div>
              )}
              {isConnected && (
                <div className="floating-start-button">
                  <button
                    className="disconnect-call-floating"
                    onClick={async () => {
                      // Clear showcase images
                      setShowcaseImages([]);
                      setShowcaseCategory('');

                      // Disconnect client
                      if (client) {
                        try {
                          await client.disconnect();
                        } catch (error) {
                          console.error("Error disconnecting:", error);
                        }
                      }
                    }}
                  >
                    <i className="fas fa-phone-slash"></i>
                    <span>End Conversation</span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Showcase images overlay - positioned at overlay level for full control */}
      {showcaseImages.length > 0 && (
        <>
          {showcaseCategory && (
            <div style={{
              position: 'absolute',
              top: '50px',
              left: '50%',
              transform: 'translateX(-50%)',
              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
              color: 'white',
              padding: '6px 16px',
              borderRadius: '12px',
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)',
              whiteSpace: 'nowrap',
              zIndex: 3000,
              pointerEvents: 'none',
            }}>
              {showcaseCategory.replace(/_/g, ' ')}
            </div>
          )}
          {showcaseImages.slice(0, 4).map((image, index) => {
            // More centered positions with larger offset from edges
            const positions = [
              { top: '120px', left: '80px' },
              { top: '120px', right: '80px' },
              { bottom: '120px', left: '80px' },
              { bottom: '120px', right: '80px' },
            ];
            const pos = positions[index] || positions[0];
            return (
              <div key={image.id || index} style={{
                position: 'absolute',
                ...pos,
                pointerEvents: 'all',
                zIndex: 3000,
              }}>
                <div style={{
                  width: '280px',
                  background: 'rgba(15, 23, 42, 0.95)',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  border: '2px solid rgba(139, 92, 246, 0.4)',
                  boxShadow: '0 12px 48px rgba(0, 0, 0, 0.7)',
                }}>
                  <img
                    src={`/images/${image.file}`}
                    alt={image.name}
                    loading="lazy"
                    decoding="async"
                    style={{
                      width: '100%',
                      height: '180px',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                    onLoad={() => console.log(`✅ Image loaded in overlay: ${image.file}`)}
                    onError={(e) => {
                      console.error(`❌ Failed to load image: /images/${image.file}`);
                      e.target.style.display = 'none';
                    }}
                  />
                  <div style={{
                    padding: '14px 16px',
                    background: 'rgba(30, 41, 59, 0.95)',
                  }}>
                    <p style={{
                      fontSize: '1rem',
                      fontWeight: 600,
                      color: '#e2e8f0',
                      margin: '0 0 6px 0',
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}>{image.name}</p>
                    <p style={{
                      fontSize: '0.85rem',
                      color: '#94a3b8',
                      margin: 0,
                      lineHeight: 1.4,
                    }}>{image.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </>
      )}
    </div>
  );
};

const VideoPopup = ({ onClose, onStartVideoChat, translations, botConfig, botInfo }) => {
  return (
    <PipecatProvider>
      <VideoPopupContent onClose={onClose} onStartVideoChat={onStartVideoChat} translations={translations} botConfig={botConfig} botInfo={botInfo} />
    </PipecatProvider>
  );
};

export default VideoPopup;